import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "react-toastify";

const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
const mic = SpeechRecognition ? new SpeechRecognition() : null;

if (mic) {
    mic.continuous = true;
    mic.interimResults = true;
    mic.lang = "en-IN";
}

export const useSpeechRecognition = (inputRef, setMessage) => {
    const [isListening, setIsListening] = useState(false);
    const [isRecognizing, setIsRecognizing] = useState(false);
    const [micSupported] = useState(!!mic);

    const isListeningRef = useRef(isListening);
    useEffect(() => {
        isListeningRef.current = isListening;
    }, [isListening]);

    useEffect(() => {
        if (!mic) return;

        mic.onstart = () => {
            setIsRecognizing(true);
        };

        mic.onend = () => {
            setIsRecognizing(false);
            if (isListeningRef.current) {
                try {
                    mic.start();
                } catch (error) {
                    console.error("Mic restart error:", error);
                }
            }
        };

        mic.onresult = (event) => {
            const currentTranscript = event.results[event.results.length - 1][0].transcript;
            setMessage(currentTranscript);
            if (inputRef.current) inputRef.current.focus();
        };

        mic.onerror = (event) => {
            let errorMsg = `Microphone error: ${event.error}`;
            if (event.error === "not-allowed") {
                errorMsg = "Microphone access denied. Please enable permissions.";
            } else if (event.error === "no-speech") {
                return; // Ignore no-speech errors to prevent spamming
            }
            toast.error(errorMsg);
            setIsListening(false);
            setIsRecognizing(false);
        };

        return () => {
            try {
                mic.stop();
            } catch (e) {}
        };
    }, [inputRef, setMessage]);

    const toggleMic = useCallback(() => {
        if (!micSupported) {
            toast.error("Speech recognition not supported in this browser.");
            return;
        }

        const nextState = !isListening;
        setIsListening(nextState);

        if (nextState) {
            try {
                mic.start();
            } catch (error) {
                console.error("Mic start error:", error);
                setIsListening(false);
            }
        } else {
            try {
                mic.stop();
            } catch (error) {
                console.error("Mic stop error:", error);
            }
        }
    }, [isListening, micSupported]);

    return { isListening, toggleMic, isRecognizing, micSupported };
};